<template>
  <!--
    Drawer/modal host for card quick-actions. Rendered at the page level and
    layered OVER the assistant, so the conversation and the workspace stay
    mounted and untouched behind it; closing returns the user exactly where
    they were.
  -->
  <template v-if="surface">
    <!-- Drawers own their own SlideOverDrawer/visible model, so just mount it. -->
    <component
      v-if="surface.mode === 'drawer'"
      :is="surface.component"
      v-bind="surface.props"
      :visible="true"
      data-testid="ai-action-drawer"
      @update:visible="onVisible"
      @close="close"
      @recorded="close"
      @created="close"
      @saved="close"
    />

    <Dialog
      v-else
      :visible="true"
      modal
      :header="surface.title"
      :style="{ width: '48rem', maxWidth: '92vw' }"
      data-testid="ai-action-modal"
      @update:visible="onVisible"
    >
      <component :is="surface.component" v-bind="surface.props" @close="close" @saved="close" />
    </Dialog>
  </template>
</template>

<script setup lang="ts">
import type { AiActionOutcome } from './aiCardActions';

/** The currently-open drawer/modal, or null when nothing is open. */
type Surface = Extract<AiActionOutcome, { mode: 'drawer' | 'modal' }>;

defineProps<{ surface: Surface | null }>();

const emit = defineEmits<{ (e: 'close'): void }>();

const close = () => emit('close');

/**
 * Child drawers/dialogs close by emitting update:visible=false.
 *
 * Verified in a browser: the click that opens the surface does NOT reach this
 * as a dismiss. PrimeVue's Drawer only self-dismisses on a mask *mousedown*
 * (and binds an outside-click listener solely when `modal` is false, which our
 * drawers are not), so the originating click cannot close it on arrival.
 */
const onVisible = (value: boolean) => {
  if (!value) close();
};
</script>
